"use client";
import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLanguage } from "./LanguageProvider";
import { useNavigate } from "react-router-dom";

// Scroll Animation Component
const ScrollAnimation = ({ children, delay = 0, direction = "up" }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const variants = {
    up: { initial: { opacity: 0, y: 50 }, animate: { opacity: 1, y: 0 } },
    down: { initial: { opacity: 0, y: -50 }, animate: { opacity: 1, y: 0 } },
    left: { initial: { opacity: 0, x: -50 }, animate: { opacity: 1, x: 0 } },
    right: { initial: { opacity: 0, x: 50 }, animate: { opacity: 1, x: 0 } },
  };

  return (
    <motion.div
      ref={ref}
      initial={variants[direction].initial}
      animate={inView ? variants[direction].animate : variants[direction].initial}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

export const Herosection = () => {
  const navigate = useNavigate();
  const { currentLang, isInitialized } = useLanguage();
  const isRTL = currentLang === "ar";

  // Translations
  const translations = {
    en: {
      badge: "Hi, Let's Work Together",
      title: "Protect your business from",
      titleLine2: "legal pitfalls",
      description:
        "Mitigate Risks with Trusted Corporate Lawyers & Legal Consultants in Dubai & UAE. Our expert corporate lawyers provide strategic counsel, compliance, and dispute resolution services across Dubai and the UAE.",
      email: "info@ruknalmurooj.com",
      phone: "+971 50 333 0841",
      btnConsultation: "Book Consultation",
      btnLearnMore: "Learn More",
      stats: [
        { value: "15+", label: "YEARS EXPERIENCE" },
        { value: "500+", label: "CLIENTS" },
        { value: "1,000+", label: "CONTRACTS DRAFTED" },
      ],
      aboutBadge: "About Us",
      aboutTitle: "We Provide Professional",
      aboutTitleLine2: "Documents Clearing Services",
      aboutDesc1:
        "RUKN ALMUROOJ Documents Clearing Services provides integrated solutions for government and judicial administrative transactions with accuracy and professionalism.",
      aboutDesc2:
        "We are committed to providing distinguished services according to the highest quality and transparency standards, without practicing law or judicial representation.",
      aboutPoints: [
        // "Licensed by Creative City – Fujairah",
        "Expert team led by Hatem Elsegaei",
        "100% Administrative & Documentation Services",
        "Fast & Reliable Service Delivery",
      ],
      aboutBtn: "Learn More About Us",
      trustedPartner: "Your Trusted Partner",
      trustedDesc:
        "We ensure seamless documentation and legal administrative support across Dubai & UAE.",
    },
    ar: {
      badge: "مرحباً، دعنا نعمل معاً",
      title: "احمِ عملك من",
      titleLine2: "المشاكل القانونية",
      description:
        "قلل المخاطر مع مستشارين قانونيين موثوقين في دبي والإمارات. خبراؤنا يقدمون استشارات استراتيجية والامتثال وحل النزاعات في جميع أنحاء دبي والإمارات.",
      email: "info@ruknalmurooj.com",
      phone: "+971 50 333 0841",
      btnConsultation: "احجز استشارة",
      btnLearnMore: "اعرف أكثر",
      stats: [
        { value: "١٥+", label: "سنوات خبرة" },
        { value: "٥٠٠+", label: "عميل" },
        { value: "١,٠٠٠+", label: "عقد تم إعداده" },
      ],
      aboutBadge: "من نحن",
      aboutTitle: "نقدم خدمات احترافية",
      aboutTitleLine2: "لتصفية المعاملات",
      aboutDesc1:
        "يقدم ركن المروج لخدمات تصفية المعاملات حلولاً متكاملة للمعاملات الحكومية والقضائية الإدارية بدقة واحترافية.",
      aboutDesc2:
        "نلتزم بتقديم خدمات متميزة وفق أعلى معايير الجودة والشفافية، دون ممارسة المحاماة أو التمثيل القضائي.",
      aboutPoints: [
        "مرخص من مدينة كريتيف – الفجيرة",
        "فريق خبراء بقيادة حاتم الصغيري",
        "خدمات إدارية وتوثيق بنسبة ١٠٠٪",
        "خدمة سريعة وموثوقة",
      ],
      aboutBtn: "اعرف أكثر عنا",
      trustedPartner: "شريكك الموثوق",
      trustedDesc:
        "نضمن لك توثيقاً سلساً ودعماً إدارياً وقانونياً في جميع أنحاء دبي والإمارات.",
    },
  };

  const t = translations[currentLang];

  const handleNavigation = () => {
    navigate("/about");
  };

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <Box>
      {/* ==================== Hero Section with CURVE at bottom ==================== */}
      <Box
        id="home"
        sx={{
          position: "relative",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          pt: { xs: 10, md: 12 },
          pb: { xs: 12, md: 15 },
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "100px",
            background: "#ffffff",
            borderTopLeftRadius: { xs: "30px", md: "60px" },
            borderTopRightRadius: { xs: "30px", md: "60px" },
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            {/* LEFT SIDE - Text Content */}
            <Grid item xs={12} md={12}>
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    bgcolor: "rgba(201,160,61,0.1)",
                    borderRadius: "50px",
                    px: 2,
                    py: 0.5,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#c9a03d", fontWeight: 500 }}>
                    {t.badge}
                  </Typography>
                </Box>

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem", lg: "3.5rem" },
                    fontWeight: "bold",
                    color: "#1a1a2e",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  {t.title}
                  <br />
                  {t.titleLine2}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "#666",
                    fontSize: "1rem",
                    lineHeight: 1.7,
                    mb: 4,
                    maxWidth: "90%",
                  }}
                >
                  {t.description}
                </Typography>

                <Stack spacing={2} sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <EmailIcon sx={{ color: "#c9a03d" }} />
                    <Typography sx={{ color: "#333" }}>{t.email}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <PhoneIcon sx={{ color: "#c9a03d" }} />
                    <Typography sx={{ color: "#333" }}>{t.phone}</Typography>
                  </Box>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    onClick={scrollToContact}
                    sx={{
                      bgcolor: "#c9a03d",
                      "&:hover": { bgcolor: "#a07d2e" },
                      borderRadius: "30px",
                      px: 4,
                      py: 1.5,
                      fontSize: "1rem",
                    }}
                  >
                    {t.btnConsultation}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleNavigation}
                    sx={{
                      borderColor: "#c9a03d",
                      color: "#c9a03d",
                      borderRadius: "30px",
                      px: 4,
                      py: 1.5,
                      "&:hover": {
                        borderColor: "#a07d2e",
                        color: "#a07d2e",
                        backgroundColor: "rgba(201,160,61,0.05)",
                      },
                    }}
                  >
                    {t.btnLearnMore}{" "}
                    {!isRTL && <ArrowForwardIcon sx={{ ml: 1, fontSize: 18 }} />}
                    {isRTL && <ArrowForwardIcon sx={{ mr: 1, fontSize: 18, transform: "rotate(180deg)" }} />}
                  </Button>
                </Stack>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ==================== STATS Section ==================== */}
      <Box id="stats" sx={{ bgcolor: "#ffffff", py: 6, borderBottom: "1px solid #f0f0f0" }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {t.stats.map((stat, index) => (
              <Grid item xs={12} sm={4} key={index} textAlign="center">
                <ScrollAnimation direction="up" delay={index * 0.1}>
                  <Typography variant="h2" sx={{ fontSize: "3rem", fontWeight: 800, color: "#c9a03d" }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a1a2e" }}>
                    {stat.label}
                  </Typography>
                </ScrollAnimation>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== About Us Section ==================== */}
      <Box id="about" sx={{ bgcolor: "#ffffff", py: 8 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center" direction={isRTL ? "row-reverse" : "row"}>
            <Grid item xs={12} md={6}>
              <ScrollAnimation direction={isRTL ? "right" : "left"}>
                <Box
                  sx={{
                    background: "linear-gradient(145deg, #f8f6f0, #fff)",
                    borderRadius: "28px",
                    p: 4,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h5" sx={{ mt: 2, fontWeight: 600 }}>
                    {t.trustedPartner}
                  </Typography>
                  <Typography sx={{ color: "#666", mt: 1 }}>{t.trustedDesc}</Typography>
                </Box>
              </ScrollAnimation>
            </Grid>
            <Grid item xs={12} md={6}>
              <ScrollAnimation direction={isRTL ? "left" : "right"}>
                <Box
                  sx={{
                    display: "inline-flex",
                    bgcolor: "rgba(201,160,61,0.1)",
                    borderRadius: "50px",
                    px: 2,
                    py: 0.5,
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#c9a03d", fontWeight: 500 }}>
                    {t.aboutBadge}
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
                    fontWeight: "bold",
                    color: "#1a1a2e",
                    mb: 3,
                  }}
                >
                  {t.aboutTitle}
                  <br />
                  {t.aboutTitleLine2}
                </Typography>
                <Typography variant="body1" sx={{ color: "#666", lineHeight: 1.8, mb: 3 }}>
                  {t.aboutDesc1}
                </Typography>
                <Typography variant="body1" sx={{ color: "#666", lineHeight: 1.8, mb: 4 }}>
                  {t.aboutDesc2}
                </Typography>
                <Stack spacing={2}>
                  {t.aboutPoints.map((point, idx) => (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <CheckCircleIcon sx={{ color: "#c9a03d" }} />
                      <Typography sx={{ color: "#333" }}>{point}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Button
                  variant="contained"
                  onClick={handleNavigation}
                  sx={{
                    bgcolor: "#c9a03d",
                    "&:hover": { bgcolor: "#a07d2e" },
                    borderRadius: "30px",
                    px: 4,
                    py: 1.5,
                    mt: 4,
                  }}
                >
                  {t.aboutBtn}
                </Button>
              </ScrollAnimation>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Herosection;